import { useEffect, useState } from 'react';
import { AlertTriangle, WifiOff, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getLicense } from '@/lib/license';
import { ApiError } from '@/lib/api';
import { avisoConexao, avisoLicenca, type Aviso } from '@/lib/avisos';
import { useRealtimeStore } from '@/lib/realtime';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/cn';

/** Faixas de aviso do app logado: conectividade e licença do cliente ativo. */
export function AvisosApp() {
  return (
    <>
      <ConexaoBanner />
      <LicencaBanner />
    </>
  );
}

function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** O hub de notificações fica conectado a sessão inteira: é o termômetro do tempo real no app. */
function ConexaoBanner() {
  const estado = useRealtimeStore((s) => s.estados.notifications);
  const online = useOnline();
  const aviso = avisoConexao(estado, online);
  if (!aviso) return null;
  return <Faixa aviso={aviso} icone={<WifiOff className="h-4 w-4 shrink-0" />} />;
}

const CHAVE_DISPENSA = 'vyxora.avisoLicencaDispensado';

function LicencaBanner() {
  const user = useAuthStore((s) => s.user);
  const hospitalId = user?.hospitalId;
  const isAdmin = user?.papel === 'HospitalAdmin' || user?.papel === 'GlobalAdmin';

  // Mesma chave do useLicense: a tela de admin e a faixa compartilham o cache.
  const { data, error } = useQuery({
    queryKey: ['license', 'self'],
    queryFn: () => getLicense(),
    enabled: !!hospitalId,
    retry: false,
    staleTime: 10 * 60_000,
    // 404 NO_LICENSE vira faixa, não toast.
    meta: { silent: true },
  });

  const semLicenca = error instanceof ApiError && error.status === 404;
  const aviso = hospitalId ? avisoLicenca(data, semLicenca, isAdmin) : null;

  // O alerta de vencimento pode ser dispensado até o fim da sessão do navegador, por cliente e
  // data de validade (renovar a licença traz o aviso de volta se voltar a vencer). Erro não se dispensa.
  const chave = `${hospitalId}:${data?.validaAte ?? ''}`;
  const [dispensado, setDispensado] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(CHAVE_DISPENSA);
    } catch {
      return null;
    }
  });

  if (!aviso || (aviso.nivel === 'alerta' && dispensado === chave)) return null;

  const dispensar =
    aviso.nivel === 'alerta'
      ? () => {
          try {
            sessionStorage.setItem(CHAVE_DISPENSA, chave);
          } catch {
            /* sessionStorage indisponível: dispensa só nesta renderização */
          }
          setDispensado(chave);
        }
      : undefined;

  return <Faixa aviso={aviso} icone={<AlertTriangle className="h-4 w-4 shrink-0" />} onDispensar={dispensar} />;
}

function Faixa({ aviso, icone, onDispensar }: { aviso: Aviso; icone: React.ReactNode; onDispensar?: () => void }) {
  const { t } = useTranslation('app');
  const texto = aviso.contato
    ? `${t(aviso.chave, aviso.params)} ${t(`avisos.contato.${aviso.contato}`)}`
    : t(aviso.chave, aviso.params);
  return (
    <div
      role={aviso.nivel === 'erro' ? 'alert' : 'status'}
      className={cn(
        'flex items-center gap-2 border-b px-6 py-2 text-sm',
        aviso.nivel === 'erro'
          ? 'border-red-500/30 bg-red-500/10 text-red-200'
          : 'border-yellow-500/30 bg-yellow-500/10 text-yellow-200',
      )}
    >
      {icone}
      <span className="flex-1">{texto}</span>
      {onDispensar && (
        <button onClick={onDispensar} aria-label={t('avisos.dispensar')}className="rounded p-1 hover:bg-white/10">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
